import { useEffect, useState } from "react";
import { ActionFunctionArgs, json, LoaderFunction } from "@remix-run/node";
import { MetaFunction, useLoaderData, useLocation } from "@remix-run/react";
import {
  ADDRESS,
  ADDRESS_CITY,
  AUTHOR,
  BRAND,
  dateModified,
  datePublished,
  DESCRIPTION_PROD_SCHEME,
  LOGO,
  OG_IMAGE,
  POSTAL_CODE,
  priceValidUntil,
  PROFILE_FACEBOOK,
  PROFILE_IMAGE,
  PROFILE_INSTAGRAM,
  PROFILE_LINKEDIN,
  SITE_URL,
  SPAM_WORD_1,
  TWITTER_IMAGE,
  WHATSAPP_MESSAGE,
  WHATSAPP_NUM,
} from "~/assets/profile/profile";
import { HomeSectionHero } from "../home/components/HomeSectionHero";
import {
  InterfaceBreadcrumbItem,
  InterfaceJasaByDistrict,
} from "~/types/GlobalInterfaces";
import GlobalBreadcrumb from "~/components/global/GlobalBreadcrumb";
import GlobalMetaScripts from "~/components/global/GlobalMetaScripts";
import { parseWithZod } from "@conform-to/zod";
import { GlobalFormSchema } from "~/scheme/global_form_schema";
import GlobalCardByDistrict from "~/components/global/GlobalCardByDistrict";
import ExploreJasaByCitySection from "~/components/global/ExploreJasaByCitySection";
import { dataJasaRenovasiRumahByDistrictBekasi } from "~/api/data-jasa-renovasi-rumah-by-district-bekasi-by-services/dataJasaRenovasiRumahByDistrictBekasi";
import GlobalCompoVideo from "~/components/global/GlobalCompoVideo";
import GlobalSeoDescriptionContent from "~/components/global/GlobalSeoDescriptionContent";
import { GlobalScrollCardAllServices } from "~/components/global/GlobalScrollCardAllServices";
import GlobalSectionHero from "~/components/global/GlobalSectionHero";
import { Navbar } from "~/components/layouts/Navbar";
import { Footer } from "~/components/layouts/footer/Footer";
export const loader: LoaderFunction = async () => {
  const breadcrumbItems = [
    { label: "Home", path: "/" },
    {
      label: "Jasa Renovasi Rumah Di Bekasi",
      path: "/jasa-renovasi-rumah-di-bekasi",
    },
  ];

  const dynamicUrls = dataJasaRenovasiRumahByDistrictBekasi.map(
    (jasa_renovasi_rumah: { district: any; slug: any }) => ({
      label: `Jasa Renovasi Rumah ${jasa_renovasi_rumah.district}`,
      path: `/jasa-renovasi-rumah-di-bekasi/${jasa_renovasi_rumah.slug}`,
    })
  );

  return json({
    dataJasaRenovasiRumahByDistrictBekasi:
      dataJasaRenovasiRumahByDistrictBekasi,
    breadcrumbItems,
    dynamicUrls,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: BRAND,
      url: SITE_URL,
      logo: LOGO,
      sameAs: [PROFILE_FACEBOOK, PROFILE_INSTAGRAM, PROFILE_LINKEDIN],
    },

    // BUSINESSCHEMA
    businessSchema: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: BRAND,
      image: LOGO,
      address: {
        "@type": "PostalAddress",
        streetAddress: ADDRESS,
        addressLocality: ADDRESS_CITY,
        postalCode: POSTAL_CODE,
        addressCountry: "ID",
      },
      telephone: WHATSAPP_NUM,
      priceRange: "Rp500.000 - Rp2.000.000",
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "18:00",
        },
      ],
    },
    // SERVICECHEMA
    serviceSchema: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: SPAM_WORD_1,
      provider: {
        "@type": "Organization",
        name: AUTHOR,
        url: SITE_URL,
      },
    },
    // PRODUCTSCHEMA
    productSchema: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: SPAM_WORD_1,
      description: DESCRIPTION_PROD_SCHEME,
      brand: {
        "@type": "Brand",
        name: AUTHOR,
      },
      sku: "GR-001",
      gtin: "123456789012",
      image: PROFILE_IMAGE,
      offers: {
        "@type": "Offer",
        price: "2000000",
        priceCurrency: "IDR",
        availability: "https://schema.org/InStock",
        url: SITE_URL,
        priceValidUntil: priceValidUntil,
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingRate: {
            "@type": "MonetaryAmount",
            value: "10000",
            currency: "IDR",
          },
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "ID",
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: {
              "@type": "QuantitativeValue",
              minValue: 1,
              maxValue: 2,
              unitCode: "DAY",
            },
            transitTime: {
              "@type": "QuantitativeValue",
              minValue: 2,
              maxValue: 4,
              unitCode: "DAY",
            },
          },
        },
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "ID",
          returnPolicyCategory:
            "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: 7,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/FreeReturn",
        },
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        reviewCount: "2248",
      },
    },
    // WebPage Schema
    webPageSchema: {
      "@context": "http://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/jasa-renovasi-rumah-di-bekasi`,
      url: `${SITE_URL}/jasa-renovasi-rumah-di-bekasi`,
      name: "Jasa Renovasi Rumah di Bekasi - Profesional & Terpercaya | BangunRumahKita",
      description:
        "Layanan jasa Renovasi Rumah di Bekasi dengan tim profesional, harga transparan, dan kualitas terjamin. Konsultasi gratis dan penawaran spesial hari ini!",
      datePublished: datePublished,
      dateModified: dateModified,
      author: {
        "@type": "Organization",
        name: AUTHOR,
        url: SITE_URL,
      },
    },

    // BreadcrumbList Schema
    breadcrumbSchema: {
      "@context": "http://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Jasa Renovasi Rumah Di Bekasi",
          item: `${SITE_URL}/jasa-renovasi-rumah-di-bekasi`,
        },
      ],
    },
  });
};
export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const { payload } = parseWithZod(formData, { schema: GlobalFormSchema });

  formData.append("Date", new Date().toISOString());
  formData.append("Whatsapp", String(payload?.whatsapp));
  return json({ msg: "success" });
};
export const meta: MetaFunction = ({ data }) => {
  const title =
    `⭐️ NO 1 Ahlinya Renovasi Rumah Di Bekasi | WA ${WHATSAPP_NUM}` ||
    `⭐️ NO 1 Ahlinya Renovasi Rumah | WA 085726726256 | Jasa Renovasi Rumah Di Bekasi`;
  const description = `⭐️ Gratis Konsultasi ! Butuh jasa Renovasi Rumah di Bekasi? Dapatkan layanan jasa renovasi profesional dengan hasil rapi, cepat, dan harga terjangkau! Hubungi kami sekarang untuk konsultasi GRATIS!`;

  const slug = `${SITE_URL}/jasa-renovasi-rumah-di-bekasi` || `${SITE_URL}`;
  const keywords = [
    "Jasa Renovasi Rumah di Bekasi",
    "Jasa Bangun Apartemen di Bekasi",
    "Jasa Renovasi Rumah di Bekasi",
    "Jasa Renovasi Apartemen di Bekasi",
    "Jasa Kontraktor Apartemen di Bekasi",
    "Jasa Renovasi Rumah Harian di Bekasi",
    "Jasa Renovasi Rumah Harian di Bekasi",
  ].join(", ");

  const rating = "5.0";
  const author = AUTHOR;
  const publisher = AUTHOR;

  return [
    { title: `${title} | arsinesia.com` },
    {
      name: "description",
      content: description,
    },
    {
      name: "keywords",
      content: keywords,
    },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: `${slug}` },
    { property: "og:image", content: OG_IMAGE },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    { name: "twitter:image", content: TWITTER_IMAGE },
    { name: "viewport", content: "width=device-width, initial-scale=1.0" },
    { name: "rating", content: rating },
    { name: "author", content: author },
    { name: "publisher", content: publisher },
    { name: "datePublished", content: datePublished },
    { name: "dateModified", content: dateModified },
    { name: "robots", content: "index, follow" },
    {
      name: "robots",
      content: "max-snippet:-1, max-image-preview:large, max-video-preview:-1",
    },
    {
      property: "og:rating",
      content: rating,
    },
    {
      property: "og:author",
      content: author,
    },
    {
      property: "og:publisher",
      content: publisher,
    },
    {
      property: "og:datePublished",
      content: datePublished,
    },
    {
      property: "og:dateModified",
      content: dateModified,
    },
    {
      property: "schema.org/Organization",
      content: JSON.stringify({
        "@context": "http://schema.org",
        "@type": "Organization",
        name: publisher,
        url: SITE_URL,
        logo: LOGO,
        sameAs: PROFILE_FACEBOOK,
      }),
    },
  ];
};

export default function Index() {
  const {
    dataJasaRenovasiRumahByDistrictBekasi,
    breadcrumbItems,
    structuredData,
    serviceSchema,
    businessSchema,
    productSchema,
  } = useLoaderData<{
    dataJasaRenovasiRumahByDistrictBekasi: InterfaceJasaByDistrict[];
    breadcrumbItems: InterfaceBreadcrumbItem[];
    dynamicUrls: { label: string; path: string }[];
    structuredData: any;
    serviceSchema: any;
    businessSchema: any;
    productSchema: any;
  }>();

  const toggleContact = () => {
    const phoneNumber = WHATSAPP_NUM;
    const message = encodeURIComponent(WHATSAPP_MESSAGE);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappURL, "_blank");
  };

  // 🔴🔴🔴 CURRENT PAGE
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState<string | undefined>();
  useEffect(() => {
    const fullPage = `${SITE_URL}${location.pathname}`;
    setCurrentPage(fullPage);
  }, [location.pathname]);
  if (!currentPage) return null;
  // Filter list berdasarkan kota

  return (
    <>
      <Navbar />
      <GlobalMetaScripts
        structuredData={structuredData}
        serviceSchema={serviceSchema}
        businessSchema={businessSchema}
        productSchema={productSchema}
      />
      <GlobalBreadcrumb items={breadcrumbItems} />
      <GlobalCompoVideo />

      <section className="w-global mx-auto">
        <div className="pt-[3rem]">
          <section className="h-[100vh]">
            <GlobalSectionHero />
          </section>
          {dataJasaRenovasiRumahByDistrictBekasi.length > 0 && (
            <GlobalSeoDescriptionContent
              slug={"Jasa Renovasi Rumah di Bekasi"}
              district={"arsinesia.com"}
              currentPage={currentPage}
            />
          )}
          <section className="">
            <section className={`pt-[2rem] lg:pt-[5rem] `}>
              <h1 className="text-2xl font-bold mb-5">
                Jasa Renovasi Rumah Di Bekasi | arsinesia.com
              </h1>
              <GlobalCardByDistrict
                data={dataJasaRenovasiRumahByDistrictBekasi}
                currentPage={currentPage}
                serviceName="Jasa Renovasi Rumah"
                basePath="/jasa-renovasi-rumah-di-bekasi"
              />
            </section>
          </section>
        </div>
      </section>
      <section className="m-global mx-auto relative overflow-hidden no-scrollbar">
        <ExploreJasaByCitySection />
        <GlobalScrollCardAllServices
          data={dataJasaRenovasiRumahByDistrictBekasi}
        />
      </section>
      <Footer />
    </>
  );
}
